import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './i18n';
import ScrollToTop from './components/ScrollToTop';
import HomePage from './pages/HomePage';
import LoginPage from './admin/auth/LoginPage';
import ProductListPage from './shop/ProductListPage';
import ProductDetailPage from './shop/ProductDetailPage';
import CheckoutPage from './shop/CheckoutPage';
import OrderConfirmationPage from './shop/OrderConfirmationPage';
import ReferralLandingPage from './referral/ReferralLandingPage';
import ReferralDashboardPage from './referral/ReferralDashboardPage';
import DealerApplyPage from './dealer/DealerApplyPage';
import ProductRecommendPopup from './components/ProductRecommendPopup';
import { useEffect, Suspense, lazy } from 'react';
import { captureAttributionFromUrl } from './lib/referralStorage';

// Lazy-load admin pages so general visitors don't download the admin bundle.
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const AdminCmsPage = lazy(() => import('./admin/cms/AdminCmsPage'));
const AdminMediaPage = lazy(() => import('./admin/media/AdminMediaPage'));
const AdminProductsPage = lazy(() => import('./admin/products/AdminProductsPage'));
const AdminPopupPage = lazy(() => import('./admin/popup/AdminPopupPage'));
const AdminOrdersPage = lazy(() => import('./admin/orders/AdminOrdersPage'));
const AdminOrderDetailPage = lazy(() => import('./admin/orders/AdminOrderDetailPage'));
const AdminSettingsPage = lazy(() => import('./admin/settings/AdminSettingsPage'));
const AdminFulfillmentPage = lazy(() => import('./admin/fulfillment/AdminFulfillmentPage'));
const AdminFulfillmentDetailPage = lazy(() => import('./admin/fulfillment/AdminFulfillmentDetailPage'));
const JourneyListPage = lazy(() => import('./admin/journeys/JourneyListPage'));
const JourneyEditor = lazy(() => import('./admin/journeys/JourneyEditor'));
const AdminReferralsPage = lazy(() => import('./admin/referrals/AdminReferralsPage'));

function App() {
  // Capture ?ref=/?dealer= attribution once on first load of any entry page
  useEffect(() => {
    captureAttributionFromUrl();
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductListPage />} />
          <Route path="/products/:slug" element={<ProductDetailPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order/:orderNum/confirm" element={<OrderConfirmationPage />} />
          <Route path="/ref/:code" element={<ReferralLandingPage />} />
          <Route path="/r/:code" element={<ReferralDashboardPage />} />
          <Route path="/referral/:token" element={<ReferralDashboardPage />} />
          <Route path="/dealer" element={<DealerApplyPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/cms" replace />} />
            <Route path="cms" element={<AdminCmsPage />} />
            <Route path="media" element={<AdminMediaPage />} />
            <Route path="products" element={<AdminProductsPage />} />
            <Route path="orders" element={<AdminOrdersPage />} />
            <Route path="orders/:id" element={<AdminOrderDetailPage />} />
            <Route path="fulfillment" element={<AdminFulfillmentPage />} />
            <Route path="fulfillment/:id" element={<AdminFulfillmentDetailPage />} />
            <Route path="journeys" element={<JourneyListPage />} />
            <Route path="journeys/new" element={<JourneyEditor />} />
            <Route path="journeys/:id" element={<JourneyEditor />} />
            <Route path="referrals" element={<AdminReferralsPage />} />
            <Route path="popup" element={<AdminPopupPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <ProductRecommendPopup />
    </BrowserRouter>
  );
}

export default App;
